import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import './login.css';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext.jsx';
import { useImagesReady } from '@/shared/hooks/useImagesReady';
import { get_base_url } from '@/shared/http/client';
import '@/shared/styles/ArtworkReveal.css';
import {
    Button,
    ButtonProvider,
    Modal as SparkModal,
    SparkIcon,
    SparkModalPortal,
    Text,
    sparkToast,
} from '@spark';

const SUPPORT_EMAIL = 'support@specific.ai';

const GRADIENT_SRC = '/images/login/gradient.png';
const ASTRONAUT_SRC = '/images/login/illustration.png';

/**
 * The two artwork layers (Figma 9026:9141) are revealed together once both
 * have decoded, so the gradient does not appear seconds before the astronaut.
 */
export const LOGIN_ARTWORK_SOURCES = [GRADIENT_SRC, ASTRONAUT_SRC];

const PROVIDER_BUTTONS = [
    { key: 'GOOGLE', provider: 'google', label: 'Sign in with Google' },
    { key: 'AZURE', provider: 'microsoft', label: 'Sign in with Microsoft' },
    { key: 'OKTA', provider: 'okta', label: 'Sign in with Okta' },
];

const providersFromDebug = (info) => {
    if (Array.isArray(info?.enabled_providers)) {
        return info.enabled_providers.map((p) => String(p).toUpperCase());
    }
    const method = String(info?.auth_method || 'GOOGLE').toUpperCase();
    if (method === 'ALL') return ['GOOGLE', 'AZURE', 'OKTA'];
    if (method === 'BOTH') return ['GOOGLE', 'AZURE'];
    if (method === 'OKTA') return ['OKTA'];
    if (method === 'GOOGLE' || method === 'AZURE') return [method];
    return [];
};

const isSafeNextPath = (path) =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

/**
 * Width of the Figma frame (MVP-DESIGNES 9026:9141, 1920 x 1080) the page is
 * laid out in. The whole frame is rendered at design size and scaled to the
 * viewport width (see login.css) so every element keeps the design's
 * proportions at any window size instead of the fixed-pixel layout growing on
 * smaller screens.
 */
export const LOGIN_DESIGN_WIDTH = 1920;

/** Card width in the Figma frame; the scale never lets it overflow the viewport. */
const LOGIN_CARD_WIDTH = 501;

/**
 * Below this viewport width the artwork is hidden (login.css) and the scale
 * stops following the viewport, so the card keeps its laptop proportions
 * (2/3 of the design size) instead of its type shrinking further.
 */
export const LOGIN_MIN_SCALE_WIDTH = 1280;

const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;

export const computeLoginScale = (width = viewportWidth()) => {
    if (width >= LOGIN_MIN_SCALE_WIDTH) return width / LOGIN_DESIGN_WIDTH;
    const sideMargin = 16;
    return Math.min(LOGIN_MIN_SCALE_WIDTH / LOGIN_DESIGN_WIDTH, (width - 2 * sideMargin) / LOGIN_CARD_WIDTH);
};

const useLoginScale = () => {
    const [scale, setScale] = useState(() => computeLoginScale());

    useLayoutEffect(() => {
        const update = () => setScale(computeLoginScale());
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    return scale;
};

const Login = () => {
    const { loginWithGoogle, authConfig } = useAuth();
    const scale = useLoginScale();
    const artworkReady = useImagesReady(LOGIN_ARTWORK_SOURCES);
    const enabledProviders = useMemo(() => providersFromDebug(authConfig), [authConfig]);
    const [helpOpen, setHelpOpen] = useState(false);
    const location = useLocation();

    const nextPath = useMemo(() => {
        const from = location.state?.from;
        if (from && typeof from.pathname === 'string' && from.pathname && from.pathname !== '/login') {
            return `${from.pathname}${from.search || ''}${from.hash || ''}`;
        }
        const queryNext = new URLSearchParams(location.search).get('next');
        if (isSafeNextPath(queryNext)) {
            return queryNext;
        }
        return null;
    }, [location]);

    const redirectToProvider = useCallback((path, extraParams = []) => {
        const base = get_base_url(window.location.origin);
        const params = [...extraParams];
        if (isSafeNextPath(nextPath)) {
            params.push(`next=${encodeURIComponent(nextPath)}`);
        }
        const query = params.length ? `?${params.join('&')}` : '';
        window.location.href = `${base}${path}${query}`;
    }, [nextPath]);

    const signInHandlers = {
        GOOGLE: () => loginWithGoogle(nextPath),
        AZURE: () => redirectToProvider('/auth/azure/login', ['prompt=select_account']),
        OKTA: () => redirectToProvider('/auth/okta/login'),
    };

    const openHelp = useCallback(() => setHelpOpen(true), []);
    const closeHelp = useCallback(() => setHelpOpen(false), []);

    const copySupportEmail = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            sparkToast.success('Email copied', { compact: true });
        } catch {
            sparkToast.error('Could not copy email', { content: SUPPORT_EMAIL });
        }
    }, []);

    return (
        <div className="login-page" style={{ '--login-scale': scale }}>
            <div className="login-stage">
                <div className="login-artboard">
                    <div className={`login-illustration artwork-reveal${artworkReady ? ' is-ready' : ''}`} aria-hidden="true">
                        <img className="login-illustration-gradient" src={GRADIENT_SRC} alt="" fetchpriority="high" decoding="async" />
                        <img className="login-illustration-astronaut" src={ASTRONAUT_SRC} alt="" fetchpriority="high" decoding="async" />
                    </div>

                    <main className="login-card fs-unmask">
                        <div className="login-brand">
                            <div className="login-logo" role="img" aria-label="specific ai">
                                <img className="login-logo-brandmark" src="/images/login/brandmark.svg" alt="" />
                                <img className="login-logo-text" src="/images/login/logo-text.svg" alt="" />
                            </div>

                            <Text as="h1" variant="display-medium" color="primary" className="login-title">
                                Welcome Back!
                            </Text>
                        </div>

                        <div className="login-body">
                            <Text as="h2" variant="heading-5-semibold" color="primary" className="login-subtitle">
                                Sign into Specific AI
                            </Text>
                            <div className="login-providers">
                                {PROVIDER_BUTTONS.filter(({ key }) => enabledProviders.includes(key)).map(({ key, provider, label }) => (
                                    <ButtonProvider key={key} provider={provider} onClick={signInHandlers[key]}>
                                        {label}
                                    </ButtonProvider>
                                ))}
                            </div>
                        </div>

                        <Text as="p" variant="base-regular" color="secondary" className="login-footer">
                            Need help?{' '}
                            <Button variant="link" size="small" inline onClick={openHelp}>
                                Contact Support
                            </Button>
                        </Text>
                    </main>
                </div>
            </div>

            <SparkModalPortal open={helpOpen} onClose={closeHelp} size="text" width={600}>
                <SparkModal
                    className="login-help-modal fs-unmask"
                    title="Need help?"
                    showSubtitle={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    bodyVariant="text"
                    showFooter
                    primaryLabel="Copy Email"
                    primaryIcon={<SparkIcon slug="copy" size="medium" />}
                    tertiaryLabel="Close"
                    onPrimaryClick={copySupportEmail}
                    onTertiaryClick={closeHelp}
                    onCloseClick={closeHelp}
                >
                    <Text as="div" variant="base-regular" color="secondary">
                        For questions, bug reports, or feature requests, email us at: {SUPPORT_EMAIL}
                    </Text>
                </SparkModal>
            </SparkModalPortal>
        </div>
    );
};

export default Login;
